/**
 * The toolkit a sound is drawn with (sfx/registry.ts): small synthesis building blocks on WebAudio —
 * tones with pitch sweeps, filtered noise of several colours, ringing metal, FM bells, plucked
 * strings (Karplus–Strong) and pitch-drop thumps. Every node a sound makes goes through here, so the
 * engine knows how many there are, when the last one stops, and how to cut them all short.
 *
 * Times (`at`, `dur`) are seconds from the sound's start; frequencies are multiplied by the call's
 * pitch variation through `k.f()` (the helpers do it for `f`, `f1`, `f0`).
 */
import type { SfxCall } from './registry';

export type NoiseColor = 'white' | 'pink' | 'brown' | 'crackle';

/** Buffers made once per audio context and shared by every voice. */
export class KitShared {
  readonly noise: Record<NoiseColor, AudioBuffer>;
  private readonly plucks = new Map<string, AudioBuffer>();
  private readonly curves = new Map<number, Float32Array<ArrayBuffer>>();

  constructor(readonly ctx: BaseAudioContext) {
    const sr = ctx.sampleRate;
    const len = sr * 2;
    // A fixed seed: the same noise every time (the review export renders the same file twice).
    let s = 0x9e3779b9;
    const rnd = () => {
      s = (s + 0x6d2b79f5) | 0;
      let t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    const make = (fill: (d: Float32Array) => void): AudioBuffer => {
      const b = ctx.createBuffer(1, len, sr);
      fill(b.getChannelData(0));
      return b;
    };
    const white = make((d) => {
      for (let i = 0; i < len; i++) d[i] = rnd() * 2 - 1;
    });
    // Pink (Paul Kellet's filter): softer, fuller; brown: a rumble.
    const pink = make((d) => {
      let b0 = 0,
        b1 = 0,
        b2 = 0,
        b3 = 0,
        b4 = 0,
        b5 = 0,
        b6 = 0;
      for (let i = 0; i < len; i++) {
        const w = rnd() * 2 - 1;
        b0 = 0.99886 * b0 + w * 0.0555179;
        b1 = 0.99332 * b1 + w * 0.0750759;
        b2 = 0.969 * b2 + w * 0.153852;
        b3 = 0.8665 * b3 + w * 0.3104856;
        b4 = 0.55 * b4 + w * 0.5329522;
        b5 = -0.7616 * b5 - w * 0.016898;
        d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
        b6 = w * 0.115926;
      }
    });
    const brown = make((d) => {
      let last = 0;
      for (let i = 0; i < len; i++) {
        last = (last + 0.02 * (rnd() * 2 - 1)) / 1.02;
        d[i] = last * 3.5;
      }
    });
    // Crackle: sparse clicks of random size (ice, fire, gravel, paper).
    const crackle = make((d) => {
      for (let i = 0; i < len; i++) {
        const r = rnd();
        d[i] = r < 0.0035 ? (rnd() * 2 - 1) * (0.4 + rnd() * 0.6) : 0;
      }
      for (let i = 1; i < len; i++) d[i] = d[i]! + d[i - 1]! * 0.55;
    });
    this.noise = { white, pink, brown, crackle };
  }

  /** A plucked string's waveform (Karplus–Strong), cached by period, length and brightness. */
  pluck(f: number, dur: number, bright: number): AudioBuffer {
    const sr = this.ctx.sampleRate;
    const n = Math.max(2, Math.round(sr / f));
    const key = `${n}|${dur.toFixed(2)}|${bright.toFixed(2)}`;
    let b = this.plucks.get(key);
    if (b) return b;
    const len = Math.ceil(sr * dur);
    b = this.ctx.createBuffer(1, len, sr);
    const d = b.getChannelData(0);
    const ring = new Float32Array(n);
    let s = n * 7919;
    for (let i = 0; i < n; i++) {
      s = (s * 1103515245 + 12345) & 0x7fffffff;
      ring[i] = (s / 0x7fffffff) * 2 - 1;
    }
    // A softer pluck starts from a smoothed burst.
    for (let pass = 0; pass < Math.round((1 - bright) * 4); pass++)
      for (let i = 1; i < n; i++) ring[i] = (ring[i]! + ring[i - 1]!) * 0.5;
    // Low strings ring longer; the loss per period sets how long.
    const loss = Math.pow(0.001, 1 / Math.max(1, (dur * sr) / n));
    let idx = 0;
    let peak = 1e-6;
    for (let i = 0; i < len; i++) {
      const cur = ring[idx]!;
      const nxt = ring[(idx + 1) % n]!;
      d[i] = cur;
      ring[idx] = (cur * 0.5 + nxt * 0.5) * Math.max(loss, 0.985);
      idx = (idx + 1) % n;
      peak = Math.max(peak, Math.abs(cur));
    }
    for (let i = 0; i < len; i++) d[i] = d[i]! / peak;
    if (this.plucks.size > 96) this.plucks.clear();
    this.plucks.set(key, b);
    return b;
  }

  /** A soft-clipping curve (tanh) for grit and crunch. */
  curve(drive: number): Float32Array<ArrayBuffer> {
    const k = Math.round(drive * 10);
    let c = this.curves.get(k);
    if (!c) {
      c = new Float32Array(1024);
      const g = 1 + k / 2;
      for (let i = 0; i < 1024; i++) {
        const x = (i / 1023) * 2 - 1;
        c[i] = Math.tanh(x * g) / Math.tanh(g);
      }
      this.curves.set(k, c);
    }
    return c;
  }
}

export interface ToneOpts {
  f: number;
  /** Frequency it sweeps to over `sweep` (default: the whole length). */
  f1?: number;
  sweep?: number;
  type?: OscillatorType;
  at?: number;
  dur: number;
  vol: number;
  attack?: number;
  /** Seconds held at full volume before the decay. */
  hold?: number;
  /** Linear (not exponential) fade. */
  linear?: boolean;
  /** Vibrato: rate (Hz) and depth (fraction of the frequency). */
  vib?: [number, number];
  detune?: number;
  out?: AudioNode;
}

export interface NoiseOpts {
  color?: NoiseColor;
  at?: number;
  dur: number;
  vol: number;
  attack?: number;
  hold?: number;
  linear?: boolean;
  /** Filter: type, centre (sweeping to f1), Q. Omit `f` for raw noise. */
  type?: BiquadFilterType;
  f?: number;
  f1?: number;
  sweep?: number;
  /** A second leg of the sweep, from `f1` to this by the end (a blade passing and leaving). */
  f2?: number;
  q?: number;
  /** Tremolo: rate (Hz) and depth (0–1). */
  trem?: [number, number];
  /** Playback rate of the noise (lower = darker, grainier). */
  rate?: number;
  out?: AudioNode;
}

export interface MetalOpts {
  f: number;
  at?: number;
  dur: number;
  vol: number;
  /** Partials: frequency ratio, level, and decay as a fraction of `dur`. */
  partials?: readonly (readonly [number, number, number])[];
  /** Slow pitch drift over the ring (a gong's), as a fraction. */
  drift?: number;
  attack?: number;
  out?: AudioNode;
}

export interface FmOpts {
  f: number;
  /** Modulator frequency over the carrier's. */
  ratio: number;
  /** Modulation index at the start and the end (brightness falling away). */
  index: number;
  index1?: number;
  f1?: number;
  type?: OscillatorType;
  at?: number;
  dur: number;
  vol: number;
  attack?: number;
  out?: AudioNode;
}

export interface PluckOpts {
  f: number;
  at?: number;
  dur?: number;
  vol: number;
  /** 0 (soft, silk) … 1 (bright, steel). */
  bright?: number;
  /** A pressed-string bend: start this fraction sharp and fall into tune. */
  bend?: number;
  out?: AudioNode;
}

/** Steel ringing: inharmonic partials of a struck blade. */
export const STEEL: readonly (readonly [number, number, number])[] = [
  [1, 1, 1],
  [2.76, 0.5, 0.7],
  [5.4, 0.28, 0.45],
  [8.93, 0.12, 0.3],
];

/** A temple bell or gong: close partials that beat against each other. */
export const BELL: readonly (readonly [number, number, number])[] = [
  [1, 1, 1],
  [1.52, 0.6, 0.85],
  [2.17, 0.45, 0.7],
  [3.01, 0.3, 0.5],
  [4.13, 0.15, 0.35],
];

/** One voice being drawn: the context, its bus, and what it has scheduled. */
export class SfxKit {
  /** Every source started (for the engine to stop on a cut). */
  readonly sources: AudioScheduledSourceNode[] = [];
  /** Nodes made by this voice. */
  nodes = 0;
  /** When the last scheduled source stops (audio clock); Infinity for a loop. */
  end = 0;
  /** The source that stops last (its `ended` frees the voice). */
  last: AudioScheduledSourceNode | null = null;

  constructor(
    readonly ctx: BaseAudioContext,
    /** The voice's bus: connect anything made by hand here. */
    readonly out: AudioNode,
    /** When the sound starts (audio clock). */
    readonly t: number,
    /** Pitch multiplier of this call (its variation). */
    readonly pitch: number,
    private readonly rng: () => number,
    private readonly shared: KitShared,
    /** A loop: sources without a length run until the engine stops them. */
    readonly loop: boolean,
    readonly call: SfxCall,
  ) {}

  /** 0–1, seeded per call. */
  r(): number {
    return this.rng();
  }
  range(a: number, b: number): number {
    return a + (b - a) * this.rng();
  }
  pick<T>(list: readonly T[]): T {
    return list[Math.floor(this.rng() * list.length) % list.length]!;
  }
  /** A frequency with the call's pitch variation. */
  f(hz: number): number {
    return hz * this.pitch;
  }
  /** Strength of the call (0–1.5). */
  get i(): number {
    return this.call.intensity;
  }

  // ───── plumbing ─────

  gain(v: number, out: AudioNode = this.out): GainNode {
    const g = this.ctx.createGain();
    g.gain.value = v;
    g.connect(out);
    this.nodes++;
    return g;
  }

  filter(type: BiquadFilterType, f: number, q = 0.7, out: AudioNode = this.out): BiquadFilterNode {
    const b = this.ctx.createBiquadFilter();
    b.type = type;
    b.frequency.value = f;
    b.Q.value = q;
    b.connect(out);
    this.nodes++;
    return b;
  }

  pan(p: number, out: AudioNode = this.out): StereoPannerNode {
    const s = this.ctx.createStereoPanner();
    s.pan.value = Math.max(-1, Math.min(1, p));
    s.connect(out);
    this.nodes++;
    return s;
  }

  /** Soft clipping for crunch (drive 1–10). */
  shaper(drive: number, out: AudioNode = this.out): WaveShaperNode {
    const w = this.ctx.createWaveShaper();
    w.curve = this.shared.curve(drive);
    w.oversample = '2x';
    w.connect(out);
    this.nodes++;
    return w;
  }

  /** A short echo (a cave, a hall): delay seconds, feedback, wet level. */
  echo(time: number, feedback: number, wet: number, out: AudioNode = this.out): GainNode {
    const input = this.gain(1, out);
    const d = this.ctx.createDelay(2);
    d.delayTime.value = time;
    const fb = this.ctx.createGain();
    fb.gain.value = feedback;
    const w = this.ctx.createGain();
    w.gain.value = wet;
    input.connect(d);
    d.connect(fb).connect(d);
    d.connect(w).connect(out);
    this.nodes += 3;
    return input;
  }

  /** Wobbles a parameter: rate (Hz) and depth (in the parameter's units). */
  lfo(
    param: AudioParam,
    rate: number,
    depth: number,
    at = 0,
    dur = Infinity,
    type: OscillatorType = 'sine',
  ): void {
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.value = rate;
    const g = this.ctx.createGain();
    g.gain.value = depth;
    o.connect(g).connect(param);
    this.nodes += 2;
    this.start(o, at, dur);
  }

  private start(src: AudioScheduledSourceNode, at: number, dur: number, offset?: number): void {
    // Never in the past (a layer drawn a moment before the sound's start): WebAudio refuses negative times.
    const t0 = Math.max(0, this.t + at);
    if (src instanceof AudioBufferSourceNode) src.start(t0, offset ?? 0);
    else src.start(t0);
    if (Number.isFinite(dur)) {
      const t1 = t0 + dur + 0.03;
      src.stop(t1);
      if (t1 >= this.end) {
        this.end = t1;
        this.last = src;
      }
    } else this.end = Infinity;
    this.sources.push(src);
  }

  /** Attack, optional hold, then a fall to silence over the rest of `dur` (or held for a loop). */
  private env(
    g: AudioParam,
    at: number,
    vol: number,
    attack: number,
    dur: number,
    hold = 0,
    linear = false,
  ): void {
    const t0 = this.t + at;
    g.setValueAtTime(0, t0);
    g.linearRampToValueAtTime(vol, t0 + attack);
    if (!Number.isFinite(dur)) return;
    if (hold > 0) g.setValueAtTime(vol, t0 + attack + hold);
    const t1 = t0 + Math.max(dur, attack + hold + 0.005);
    if (linear) g.linearRampToValueAtTime(0, t1);
    else g.exponentialRampToValueAtTime(0.0001, t1);
  }

  private dur(d: number): number {
    return this.loop ? d : Number.isFinite(d) ? d : 1;
  }

  // ───── voices ─────

  /** An oscillator with a pitch sweep and an envelope. */
  tone(o: ToneOpts): OscillatorNode {
    const at = o.at ?? 0;
    const dur = this.dur(o.dur);
    const osc = this.ctx.createOscillator();
    osc.type = o.type ?? 'sine';
    const f0 = this.f(o.f);
    const t0 = this.t + at;
    osc.frequency.setValueAtTime(f0, t0);
    if (o.f1 !== undefined) {
      const f1 = Math.max(20, this.f(o.f1));
      const sweep = o.sweep ?? (Number.isFinite(dur) ? dur : 1);
      osc.frequency.exponentialRampToValueAtTime(f1, t0 + sweep);
    }
    if (o.detune) osc.detune.value = o.detune;
    const g = this.ctx.createGain();
    this.env(g.gain, at, o.vol, o.attack ?? 0.004, dur, o.hold, o.linear);
    osc.connect(g).connect(o.out ?? this.out);
    this.nodes += 2;
    if (o.vib) this.lfo(osc.frequency, o.vib[0], f0 * o.vib[1], at, dur);
    this.start(osc, at, dur);
    return osc;
  }

  /** Noise through a filter that can sweep; the workhorse of whooshes, hits, wind and water. */
  noise(o: NoiseOpts): AudioBufferSourceNode {
    const at = o.at ?? 0;
    const dur = this.dur(o.dur);
    const src = this.ctx.createBufferSource();
    src.buffer = this.shared.noise[o.color ?? 'white'];
    src.loop = true;
    if (o.rate) src.playbackRate.value = o.rate;
    const g = this.ctx.createGain();
    this.env(g.gain, at, o.vol, o.attack ?? 0.004, dur, o.hold, o.linear);
    let head: AudioNode = g;
    if (o.f !== undefined) {
      const b = this.ctx.createBiquadFilter();
      b.type = o.type ?? 'bandpass';
      b.Q.value = o.q ?? 1;
      const t0 = this.t + at;
      const f0 = Math.min(20000, this.f(o.f));
      b.frequency.setValueAtTime(f0, t0);
      if (o.f1 !== undefined) {
        const sweep = o.sweep ?? (Number.isFinite(dur) ? dur : 1);
        b.frequency.exponentialRampToValueAtTime(Math.min(20000, Math.max(30, this.f(o.f1))), t0 + sweep);
        if (o.f2 !== undefined && Number.isFinite(dur) && dur > sweep)
          b.frequency.exponentialRampToValueAtTime(Math.min(20000, Math.max(30, this.f(o.f2))), t0 + dur);
      }
      src.connect(b).connect(g);
      this.nodes++;
    } else src.connect(g);
    if (o.trem) {
      const tg = this.ctx.createGain();
      tg.gain.value = 1 - o.trem[1] / 2;
      g.connect(tg);
      head = tg;
      this.nodes++;
      this.lfo(tg.gain, o.trem[0], o.trem[1] / 2, at, dur);
    }
    head.connect(o.out ?? this.out);
    this.nodes += 2;
    this.start(src, at, dur, this.rng() * 1.5);
    return src;
  }

  /** Struck metal: a cluster of partials, each with its own decay (steel, bells, gongs, coins). */
  metal(o: MetalOpts): void {
    const parts = o.partials ?? STEEL;
    for (const [ratio, lv, decay] of parts) {
      const f = o.f * ratio;
      if (this.f(f) > 18000) continue;
      this.tone({
        f,
        f1: o.drift ? f * (1 - o.drift) : undefined,
        at: o.at,
        dur: o.dur * decay,
        vol: o.vol * lv,
        attack: o.attack ?? 0.002,
        out: o.out,
      });
    }
  }

  /** Two-operator FM: bells, jade, chimes, a screech. */
  fm(o: FmOpts): OscillatorNode {
    const at = o.at ?? 0;
    const dur = this.dur(o.dur);
    const t0 = this.t + at;
    const car = this.ctx.createOscillator();
    car.type = o.type ?? 'sine';
    const fc = this.f(o.f);
    car.frequency.setValueAtTime(fc, t0);
    if (o.f1 !== undefined) car.frequency.exponentialRampToValueAtTime(Math.max(20, this.f(o.f1)), t0 + dur);
    const mod = this.ctx.createOscillator();
    mod.frequency.setValueAtTime(fc * o.ratio, t0);
    if (o.f1 !== undefined)
      mod.frequency.exponentialRampToValueAtTime(Math.max(20, this.f(o.f1) * o.ratio), t0 + dur);
    const mg = this.ctx.createGain();
    mg.gain.setValueAtTime(fc * o.index, t0);
    if (Number.isFinite(dur))
      mg.gain.exponentialRampToValueAtTime(Math.max(0.01, fc * (o.index1 ?? o.index * 0.1)), t0 + dur);
    mod.connect(mg).connect(car.frequency);
    const g = this.ctx.createGain();
    this.env(g.gain, at, o.vol, o.attack ?? 0.003, dur);
    car.connect(g).connect(o.out ?? this.out);
    this.nodes += 4;
    this.start(car, at, dur);
    this.start(mod, at, dur);
    return car;
  }

  /** A plucked string (guzheng, guqin, pipa): Karplus–Strong, with an optional bend into tune. */
  pluck(o: PluckOpts): void {
    const at = o.at ?? 0;
    const dur = o.dur ?? 1.2;
    const f = this.f(o.f);
    const src = this.ctx.createBufferSource();
    src.buffer = this.shared.pluck(f, dur, o.bright ?? 0.6);
    const t0 = this.t + at;
    if (o.bend) {
      src.playbackRate.setValueAtTime(1 + o.bend, t0);
      src.playbackRate.exponentialRampToValueAtTime(1, t0 + 0.12);
    }
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(o.vol, t0);
    g.gain.setValueAtTime(o.vol, t0 + dur * 0.7);
    g.gain.linearRampToValueAtTime(0, t0 + dur);
    src.connect(g).connect(o.out ?? this.out);
    this.nodes += 2;
    this.start(src, at, dur);
  }

  /** A body blow: a sine dropping in pitch (a drum, a thud, a footfall's weight). */
  thump(o: { f0: number; f1: number; at?: number; dur: number; vol: number; out?: AudioNode }): void {
    this.tone({
      f: o.f0,
      f1: o.f1,
      sweep: o.dur * 0.6,
      at: o.at,
      dur: o.dur,
      vol: o.vol,
      attack: 0.002,
      out: o.out,
    });
  }

  /** A tiny click (wood, a latch, a bead). */
  click(o: { f: number; at?: number; vol: number; q?: number; dur?: number; out?: AudioNode }): void {
    this.noise({ at: o.at, dur: o.dur ?? 0.02, vol: o.vol, attack: 0.001, f: o.f, q: o.q ?? 3, out: o.out });
  }
}
